import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { X, ListChecks, Undo2, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { TaskChangeset } from './TaskChangeset';
import { TaskBoardMini } from './TaskBoardMini';
import { useIsMobile } from '../../hooks/useIsMobile';
import { cn } from '../../lib/utils';
import { isDemoted } from '../tasks/taskMeta';

/**
 * TaskAgentPanel — the interactive surface for everything the agent does to
 * tasks, plus a full editing board so the user is never a passenger.
 *
 * Two tabs:
 *   CHANGES — per-turn changesets, each row live-editable and revertable.
 *   ALL     — the whole project board, condensed, with inline create/edit/delete.
 *
 * Desktop renders as a resizable column beside the conversation; mobile takes
 * over the screen as a sheet with a sticky action bar.
 */

const TABS = [
    { id: 'changes', label: 'Changes' },
    { id: 'all', label: 'All' },
];

const MIN_WIDTH = 380;
const MAX_WIDTH = 720;

function TaskAgentPanelImpl({
    tab,
    onTabChange,
    batches = [],
    tasks = [],
    readOnly = false,
    isLoading = false,
    focusBatchId = null,
    projectBaseUrl,
    onClose,
    onCreateTask,
    onUpdateTask,
    onDeleteTask,
    onRevertChange,
    onRevertBatch,
    revertingIds,
}) {
    const isMobile = useIsMobile();
    const reduceMotion = useReducedMotion();
    const navigate = useNavigate();
    const scrollRef = useRef(null);

    // Every task the agent has touched in this conversation — drives the ✦ marker
    // on the board tab so provenance stays visible outside the changeset.
    const agentTouchedIds = useMemo(() => {
        const ids = new Set();
        for (const batch of batches) {
            if (batch.source !== 'ai') continue;
            for (const change of batch.changes) {
                if (!change.reverted) ids.add(change.task_id);
            }
        }
        return ids;
    }, [batches]);

    const activeTaskCount = useMemo(
        () => tasks.reduce((n, task) => (isDemoted(task) ? n : n + 1), 0),
        [tasks]
    );

    const totals = useMemo(() => {
        let active = 0;
        let unchanged = 0;
        let conflicted = false;
        for (const batch of batches) {
            active += batch.active;
            // The turns that wrote nothing. Counted separately because they can
            // never be reverted and must not inflate the revert affordances,
            // but they still need to say they happened.
            unchanged += (batch.noop?.length || 0) + (batch.skipped?.length || 0);
            if (batch.conflicted) conflicted = true;
        }
        return { active, unchanged, conflicted };
    }, [batches]);

    const latestBatch = batches[0];
    const canRevertLatest = !readOnly && latestBatch && latestBatch.active > 0;

    // Scroll a specific turn into view when the user clicks its chat chip.
    useEffect(() => {
        if (!focusBatchId || tab !== 'changes') return;
        const node = scrollRef.current?.querySelector(`[data-testid="changeset-${focusBatchId}"]`);
        node?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }, [focusBatchId, tab, reduceMotion, batches.length]);

    // There used to be a second effect here that forced scrollTop to 0 whenever
    // work started, so new rows couldn't appear off-screen. It has no job left:
    // the panel is now opened with `focusBatchId` pointing at the turn that just
    // landed, and the effect above scrolls to exactly that. Keeping both meant
    // the later one won and the scroll target was always the top, which is only
    // the right answer when the newest batch happens to be the one you asked for.
    const handleRevertLatest = useCallback(() => {
        if (latestBatch) onRevertBatch(latestBatch, { force: !!latestBatch.conflicted });
    }, [latestBatch, onRevertBatch]);

    const openMissionControl = useCallback(() => {
        navigate(`${projectBaseUrl}/tasks`);
        if (isMobile) onClose();
    }, [navigate, projectBaseUrl, isMobile, onClose]);

    const handleCreate = useCallback((title) => {
        onCreateTask(title);
        toast.success('Task added');
    }, [onCreateTask]);

    const activeTabIndex = TABS.findIndex((t) => t.id === tab);

    return (
        // Chrome and the enter animation both belong to the enclosing
        // ArtifactSurface now, so all four panels move identically instead of
        // this one having its own spring.
        <div
            data-testid="task-agent-panel"
            className="flex h-full min-h-0 flex-col"
        >
            {/* ─── Header ─────────────────────────────────────────────────── */}
            <div
                className={cn(
                    'flex flex-shrink-0 items-center gap-3 border-b border-white/10 bg-black/40 px-3 backdrop-blur-xl',
                    isMobile ? 'pt-safe-top pb-3' : 'py-3',
                )}
            >
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl border border-primary/30 bg-gradient-to-br from-primary/30 to-accent/20">
                    <ListChecks className="h-4 w-4 text-primary" />
                </div>

                <div className="min-w-0 flex-1">
                    <h3 className="truncate font-mono text-[11px] font-bold uppercase tracking-[0.18em]">
                        Tasks
                    </h3>
                    <p className="truncate text-[11px] text-muted-foreground">
                        {totals.active > 0 ? (
                            <>
                                <span className="font-mono tabular-nums text-foreground">{totals.active}</span> agent change
                                {totals.active === 1 ? '' : 's'} this session
                            </>
                        ) : totals.unchanged > 0 ? (
                            // Reached by asking for something that was already
                            // true. Said out loud, because the alternative is a
                            // panel that opens onto nothing.
                            'Nothing to change — already up to date'
                        ) : (
                            'Fully editable — the agent sees your edits'
                        )}
                    </p>
                </div>

                <button
                    type="button"
                    onClick={openMissionControl}
                    title="Open task board"
                    aria-label="Open task board"
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
                >
                    <ExternalLink className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="task-panel-close"
                    title="Close (Esc)"
                    aria-label="Close task panel"
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>

            {/* ─── Tabs ───────────────────────────────────────────────────── */}
            <div className="flex-shrink-0 border-b border-white/10 px-3 py-2">
                <div className="relative flex items-center gap-1 rounded-xl bg-secondary/30 p-1">
                    <div
                        aria-hidden
                        className="absolute h-[calc(100%-8px)] rounded-lg bg-primary shadow-lg transition-all duration-300 ease-out"
                        style={{
                            width: `calc(${100 / TABS.length}% - 4px)`,
                            left: `calc(${activeTabIndex * (100 / TABS.length)}% + 4px)`,
                        }}
                    />
                    {TABS.map((t) => {
                        // Parked tasks are excluded here for the same reason the
                        // mini board below hides them: the badge has to match
                        // what clicking the tab actually shows.
                        const count = t.id === 'changes' ? totals.active : activeTaskCount;
                        const isActive = t.id === tab;
                        return (
                            <button
                                key={t.id}
                                type="button"
                                data-testid={`task-panel-tab-${t.id}`}
                                onClick={() => onTabChange(t.id)}
                                className={cn(
                                    'relative z-10 flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-medium transition-colors duration-200',
                                    isActive ? 'text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                                )}
                            >
                                {t.label}
                                <span
                                    className={cn(
                                        'rounded px-1.5 py-px font-mono text-[10px] tabular-nums',
                                        isActive ? 'bg-black/20 text-primary-foreground' : 'bg-white/5',
                                    )}
                                >
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ─── Body ───────────────────────────────────────────────────── */}
            <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
                {isLoading && batches.length === 0 ? (
                    <div className="flex h-full items-center justify-center">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                ) : tab === 'changes' ? (
                    <TaskChangeset
                        batches={batches}
                        tasks={tasks}
                        readOnly={readOnly}
                        revertingIds={revertingIds}
                        onRevertChange={onRevertChange}
                        onRevertBatch={onRevertBatch}
                        onUpdateTask={onUpdateTask}
                        onDeleteTask={onDeleteTask}
                    />
                ) : (
                    <TaskBoardMini
                        tasks={tasks}
                        agentTouchedIds={agentTouchedIds}
                        readOnly={readOnly}
                        onCreate={handleCreate}
                        onUpdate={onUpdateTask}
                        onDelete={onDeleteTask}
                    />
                )}
            </div>

            {/* ─── Footer ─────────────────────────────────────────────────── */}
            <div
                className={cn(
                    'flex flex-shrink-0 items-center justify-between gap-2 border-t border-white/10 bg-black/40 px-3',
                    isMobile ? 'pb-safe-bottom pt-3' : 'py-2',
                )}
            >
                <p className="min-w-0 truncate text-[10px] text-muted-foreground">
                    {readOnly
                        ? 'Read-only preview'
                        : tab === 'changes'
                            ? 'Edit anything here — changes are saved instantly'
                            : 'The agent sees every edit you make'}
                </p>

                {canRevertLatest ? (
                    <button
                        type="button"
                        data-testid="task-panel-revert-latest"
                        onClick={handleRevertLatest}
                        className={cn(
                            'flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-white/10 px-2.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground transition-colors hover:border-red-500/30 hover:bg-red-500/15 hover:text-red-400',
                            isMobile ? 'h-11' : 'h-7',
                        )}
                    >
                        <Undo2 className="h-3 w-3" />
                        Undo last turn
                    </button>
                ) : null}
            </div>
        </div>
    );
}

export const TaskAgentPanel = memo(TaskAgentPanelImpl);
export { MIN_WIDTH as TASK_PANEL_MIN_WIDTH, MAX_WIDTH as TASK_PANEL_MAX_WIDTH };
export default TaskAgentPanel;
